import { useState, useEffect } from 'react'
import { fetchGalleryCollaborators, addGalleryCollaborator, removeGalleryCollaborator } from '../../lib/api'
import type { Collaborator } from '../../lib/types'
import LoadingDots from '../LoadingDots'

interface ManageGalleryAccessModalProps {
  galleryId: string
  galleryName: string
  onClose: () => void
}

export default function ManageGalleryAccessModal({
  galleryId,
  galleryName,
  onClose,
}: ManageGalleryAccessModalProps) {
  const [collaborators, setCollaborators] = useState<Collaborator[]>([])
  const [loading, setLoading] = useState(true)
  const [email, setEmail] = useState('')
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [removingEmail, setRemovingEmail] = useState<string | null>(null)

  const reload = async () => {
    try {
      const list = await fetchGalleryCollaborators(galleryId)
      setCollaborators(list)
      setError(null)
    } catch (err) {
      setError((err as Error).message)
    }
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const list = await fetchGalleryCollaborators(galleryId)
        if (!cancelled) {
          setCollaborators(list)
          setError(null)
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [galleryId])

  const handleAdd = async () => {
    const trimmed = email.trim().toLowerCase()
    if (!trimmed || !trimmed.includes('@')) return
    setAdding(true)
    setError(null)
    try {
      await addGalleryCollaborator(galleryId, trimmed)
      setEmail('')
      await reload()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setAdding(false)
    }
  }

  const handleRemove = async (collabEmail: string) => {
    setRemovingEmail(collabEmail)
    try {
      await removeGalleryCollaborator(galleryId, collabEmail)
      await reload()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setRemovingEmail(null)
    }
  }

  const canAdd = email.trim().length > 0 && email.includes('@') && !adding

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-70"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="w-full max-w-md bg-gray-900 rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex flex-col gap-6 px-6 pt-6 pb-6 overflow-y-auto">

          {/* Header */}
          <div className="relative flex items-center justify-center">
            <button
              type="button"
              onClick={onClose}
              className="absolute right-0 text-gray-500 hover:text-white transition-colors p-1 rounded"
              aria-label="Lukk"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-lg font-semibold text-white">Del tilgang</h2>
          </div>

          <p className="text-gray-400 text-sm text-center">
            Legg til e-postadresser for å gi andre administrasjonstilgang til "{galleryName}".
            De må opprette en konto for å få tilgang.
          </p>

          {error && (
            <div className="rounded-lg bg-red-900 border border-red-700 p-3 text-red-200 text-sm">
              {error}
            </div>
          )}

          {/* Add collaborator */}
          <div className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && canAdd) handleAdd() }}
              placeholder="epost@example.com"
              className="flex-1 bg-gray-800 text-white rounded-lg px-3 py-2.5 outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500 text-base"
            />
            <button
              type="button"
              onClick={handleAdd}
              disabled={!canAdd}
              className="px-4 py-2.5 rounded-lg bg-blue-500 hover:bg-blue-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-medium text-sm transition-colors flex items-center gap-1.5"
            >
              {adding ? <LoadingDots /> : 'Legg til'}
            </button>
          </div>

          {/* Collaborator list */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <LoadingDots />
                Laster...
              </div>
            </div>
          ) : collaborators.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              Ingen delte brukere ennå.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {collaborators.map((c) => (
                <div
                  key={c.email}
                  className="flex items-center justify-between rounded-lg bg-gray-800 px-3 py-2.5"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{c.email}</p>
                    <p className="text-xs text-gray-500">
                      {c.linkedOwnerId ? 'Koblet til konto' : 'Venter på registrering'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemove(c.email)}
                    disabled={removingEmail === c.email}
                    className="ml-2 p-1.5 text-gray-500 hover:text-red-400 disabled:opacity-40 transition-colors rounded"
                    aria-label={`Fjern ${c.email}`}
                  >
                    {removingEmail === c.email ? (
                      <LoadingDots />
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
